import { useEffect, useRef } from "react";

/**
 * Calls `callback` when the component unmounts — and ONLY then, not on
 * every render where `callback`'s identity changes. A plain
 * `useEffect(() => () => callback(), [callback])` fires its cleanup on
 * every dependency change too (React's normal cleanup-before-re-run
 * behavior), which calls a STALE `callback` — closed over whatever props/
 * state were current when that render committed — as a side effect of an
 * unrelated update, not a real unmount. A ref keeps the callback current
 * without making it a dependency, so the effect's own deps never change and
 * its cleanup only ever fires once, at true unmount.
 */
export const useCallOnUnmount = (callback: () => void) => {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    return () => {
      callbackRef.current();
    };
  }, []);
};
